/*
	Bernard James navigation behaviors:
	- BJMegaMenu: hover/keyboard-driven mega panels under the center-logo header.
	  Toggles the `bj-mega-open` class on the header section wrapper; all visual
	  state (white background, wordmark -> monogram swap) is CSS-driven.
	- BJNavDrawer: full-screen mobile drawer. Inherits open/close, focus trap,
	  Esc and scroll locking from HeaderDrawer (assets/global.js); overrides
	  bindEvents so utility buttons (contrast toggle, ship-to) don't close it.
	- Enhanced-contrast toggle persisted in localStorage.
*/
(() => {
	const OPEN_DELAY = 120;
	const CLOSE_DELAY = 200;

	class BJMegaMenu extends HTMLElement {
		connectedCallback() {
			this.sectionHeader = this.closest(".shopify-section-header");
			this.wrapper = this.closest(".header-wrapper");
			if (!this.sectionHeader || !this.wrapper) return;

			this.triggers = Array.from(
				this.wrapper.querySelectorAll(".bj-mega-trigger[data-mega-trigger]")
			);
			this.panels = Array.from(this.querySelectorAll("[data-mega-panel]"));
			this.openTimer = null;
			this.closeTimer = null;
			this.current = null;

			this.triggers.forEach((trigger) => {
				const handle = trigger.dataset.megaTrigger;

				trigger.addEventListener("mouseenter", () => {
					this.cancelClose();
					clearTimeout(this.openTimer);
					this.openTimer = setTimeout(() => this.open(handle), OPEN_DELAY);
				});
				trigger.addEventListener("mouseleave", () => {
					clearTimeout(this.openTimer);
				});
				// First tap on touch devices opens the panel instead of navigating.
				trigger.addEventListener("touchend", (event) => {
					if (this.current !== handle) {
						event.preventDefault();
						this.open(handle);
					}
				});
				trigger.addEventListener("keydown", (event) => {
					if (["ArrowDown", " ", "Spacebar"].includes(event.key)) {
						event.preventDefault();
						this.open(handle);
						this.focusFirstLink(handle);
					}
				});
			});

			this.wrapper.addEventListener("mouseleave", () => this.scheduleClose());
			this.wrapper.addEventListener("mouseenter", () => this.cancelClose());

			this.wrapper.addEventListener("keyup", (event) => {
				if (event.code === "Escape" && this.current) {
					const trigger = this.triggerFor(this.current);
					this.closeAll();
					if (trigger) trigger.focus();
				}
			});
			this.wrapper.addEventListener("focusout", () => {
				setTimeout(() => {
					if (this.current && !this.wrapper.contains(document.activeElement)) {
						this.closeAll();
					}
				});
			});

			// Panels start `hidden`, so their tiles are display:none and lazy
			// images inside them never intersect the viewport — the first hover
			// would otherwise open an empty panel and fetch the imagery then.
			// Flipping them to eager once the page is idle loads them off the
			// critical path (eager ignores visibility).
			const warmImages = () => {
				this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
					img.loading = "eager";
				});
			};
			if ("requestIdleCallback" in window) {
				requestIdleCallback(warmImages, { timeout: 3000 });
			} else {
				setTimeout(warmImages, 2000);
			}

			this.onScroll = () => {
				if (this.current && window.scrollY > this.wrapper.offsetHeight) {
					this.closeAll();
				}
			};
			window.addEventListener("scroll", this.onScroll, { passive: true });
		}

		disconnectedCallback() {
			window.removeEventListener("scroll", this.onScroll);
			this.sectionHeader?.classList.remove("bj-mega-open");
			if (this.sectionHeader) delete this.sectionHeader.dataset.bjMegaOpen;
		}

		triggerFor(handle) {
			return this.triggers.find((t) => t.dataset.megaTrigger === handle);
		}

		open(handle) {
			if (this.current === handle) return;
			this.current = handle;
			this.panels.forEach((panel) => {
				panel.hidden = panel.dataset.megaPanel !== handle;
			});
			this.triggers.forEach((trigger) => {
				trigger.setAttribute(
					"aria-expanded",
					trigger.dataset.megaTrigger === handle ? "true" : "false"
				);
			});
			this.sectionHeader.classList.add("bj-mega-open");
			this.sectionHeader.dataset.bjMegaOpen = handle;
		}

		closeAll() {
			this.current = null;
			clearTimeout(this.openTimer);
			this.cancelClose();
			this.panels.forEach((panel) => (panel.hidden = true));
			this.triggers.forEach((trigger) =>
				trigger.setAttribute("aria-expanded", "false")
			);
			this.sectionHeader.classList.remove("bj-mega-open");
			delete this.sectionHeader.dataset.bjMegaOpen;
		}

		scheduleClose() {
			this.cancelClose();
			this.closeTimer = setTimeout(() => this.closeAll(), CLOSE_DELAY);
		}

		cancelClose() {
			clearTimeout(this.closeTimer);
			this.closeTimer = null;
		}

		focusFirstLink(handle) {
			const panel = this.panels.find((p) => p.dataset.megaPanel === handle);
			panel?.querySelector("a")?.focus();
		}
	}

	const defineElements = () => {
		if (!customElements.get("bj-mega-menu")) {
			customElements.define("bj-mega-menu", BJMegaMenu);
		}

		if (typeof HeaderDrawer !== "undefined" && !customElements.get("bj-nav-drawer")) {
			class BJNavDrawer extends HeaderDrawer {
				bindEvents() {
					this.querySelectorAll("summary").forEach((summary) =>
						summary.addEventListener("click", this.onSummaryClick.bind(this))
					);
					this.querySelectorAll("button").forEach((button) => {
						if (button.closest(".bj-drawer__utility")) return;
						if (
							button.matches(
								".bj-contrast-toggle, .bj-drawer__search, .bj-drawer__close, .toggle-scheme-button, .header__localization-button, .header__localization-lang-button"
							)
						)
							return;
						button.addEventListener(
							"click",
							this.onCloseButtonClick.bind(this)
						);
					});

					// The top X fully closes the drawer (scroll lock + focus trap
					// included), unlike the inherited per-submenu close buttons.
					this.querySelectorAll(".bj-drawer__close").forEach((button) => {
						button.addEventListener("click", () => {
							this.closeMenuDrawer(
								new Event("click"),
								this.querySelector("summary")
							);
						});
					});

					// Search inside the drawer proxies the header search trigger so the
					// page keeps a single #search-icon-open element.
					this.querySelectorAll(".bj-drawer__search").forEach((button) => {
						button.addEventListener("click", () => {
							this.closeMenuDrawer(new Event("click"));
							document.getElementById("search-icon-open")?.click();
						});
					});
				}
			}
			customElements.define("bj-nav-drawer", BJNavDrawer);
		}
	};

	if (typeof HeaderDrawer === "undefined") {
		document.addEventListener("DOMContentLoaded", defineElements);
	} else {
		defineElements();
	}

	// Enhanced contrast preference.
	const CONTRAST_KEY = "bj-contrast";
	const applyContrast = (enabled) => {
		document.body.classList.toggle("bj-contrast", enabled);
		document
			.querySelectorAll(".bj-contrast-toggle")
			.forEach((button) =>
				button.setAttribute("aria-pressed", enabled ? "true" : "false")
			);
	};

	const initContrast = () => {
		applyContrast(localStorage.getItem(CONTRAST_KEY) === "1");
		document.addEventListener("click", (event) => {
			const toggle = event.target.closest(".bj-contrast-toggle");
			if (!toggle) return;
			const enabled = !document.body.classList.contains("bj-contrast");
			localStorage.setItem(CONTRAST_KEY, enabled ? "1" : "0");
			applyContrast(enabled);
		});
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initContrast);
	} else {
		initContrast();
	}
})();
